import { z } from "zod";

import {
  VIBE_LOG_GATEWAY_DEFAULT_LIMIT,
  VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH,
  VIBE_LOG_GATEWAY_MAX_LIMIT,
  VibeLogColorSchema,
  VibeLogLineSchema
} from "../../../../schemas/VibeTenant";

/**
 * The runtime-log SSE wire contract — what the console reads while a deployed
 * app is running.
 *
 * ## Why this is not a NexusApi endpoint
 *
 * The paged read of the same logs DOES ride `ZVibe` (`app-logs.schemas.ts`),
 * because the envelope (`{success, data}`) describes one body and one body is
 * what a page is. A follow cannot: the codegen has no shape for a connection
 * emitting frames over minutes. Chat settled this first and build logs took the
 * same answer (`build-log-stream.schemas.ts`); runtime logs take it too, rather
 * than inventing a third one.
 *
 * ## Frames are data-only, and the vocabulary is the build log's
 *
 * No SSE `event:` names on the console-facing side — the discriminant lives
 * INSIDE the JSON, and the three frame types are `lines` / `end` / `error`,
 * spelled exactly as {@link VibeBuildLogStreamEventSchema} spells them. A
 * console that can already read one Vibe log stream can read this one, and
 * `VibeSseFrameWriter` serves both from one implementation.
 *
 * What differs is the PAYLOAD of a `lines` frame, and it differs for a real
 * reason: a build-log line is `{id, text}` because the build buffer has no
 * clock, while a runtime line is a {@link VibeLogLineSchema} — Loki stamps it,
 * so it carries a nanosecond timestamp, an ISO instant and the deployment slot
 * it came from. Flattening the two into one line type would mean synthesising a
 * timestamp for build logs, which is the invented chronology that file refuses.
 *
 * ⚠️ The TENANT-facing side of this stream is a different wire and is NOT this
 * contract. `vibe-log-gateway` emits NAMED events (`event: lines`) with `id:`
 * resume points, per `write-sse.ts`. The control plane parses that and re-emits
 * the data-only frames below. Two hops, two wire formats, deliberately: the
 * gateway's `id:` line drives an `EventSource` reconnect INSIDE the tenant edge,
 * and handing it to a browser would resume a stream the browser cannot address.
 *
 * ## 🔴 NO FIELD NAMES A HOST, AND NONE EVER MAY
 *
 * Identical to the paged read, for identical reasons. The gateway host is
 * derived from the caller's ORGANIZATION, inside the use case, after the org
 * assertion. There is deliberately no `host`, `gateway`, `tenant`, `cluster` or
 * `url` field here, and the query schema is `.strict()` so one cannot be
 * smuggled in as an unknown key either.
 *
 * `app-log-stream-contract-carries-no-host.test.ts` asserts this by enumerating
 * the schema's own keys, so a field added later fails the suite rather than
 * quietly widening the boundary.
 */

/**
 * What a caller may ask about a follow.
 *
 * The query fields of a paged read minus `to` and `cursor`, mirroring
 * {@link VibeLogStreamRequestSchema}. `to` is absent rather than optional
 * because the end of a follow is always NOW, moving: accepting the field would
 * let a caller ask to follow a window that has already closed, which is a query
 * wearing a stream's clothes. `cursor` is absent because paging backwards and
 * following forwards are different operations.
 *
 * Every value arrives as a STRING on a query string, so the numeric fields
 * coerce. `from` is `.int()` because `z.coerce.number()` on `""` yields `0`
 * rather than a failure, and a silent 0 would read as "the epoch" — a request
 * for seven days of history dressed up as a live tail.
 *
 * The window ceiling is enforced by the gateway's own follow, which clamps
 * `from` forward to its 7-day floor rather than refusing. That is deliberate and
 * is why there is no time-dependent `refine` here: a schema whose validity
 * depends on `Date.now()` cannot be tested without freezing a clock, and the
 * clamp is the friendlier behaviour for a tab left open overnight.
 */
export const GetVibeAppLogStreamQuerySchema = z
  .object({
    /** Where the follow starts, as epoch milliseconds. Clamped by the gateway to its 7-day floor. */
    from: z.coerce.number().int().nonnegative(),
    /** Restrict to one deployment slot. Absent means both. */
    color: VibeLogColorSchema.optional(),
    /** Case-sensitive LITERAL substring. Never a regular expression. */
    contains: z.string().trim().min(1).max(VIBE_LOG_GATEWAY_MAX_CONTAINS_LENGTH).optional(),
    /** Lines per poll. */
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(VIBE_LOG_GATEWAY_MAX_LIMIT)
      .default(VIBE_LOG_GATEWAY_DEFAULT_LIMIT)
  })
  .strict();

export type GetVibeAppLogStreamQuery = z.infer<typeof GetVibeAppLogStreamQuerySchema>;

/**
 * The path variable, `.strict()` for the same reason the query is.
 *
 * Its own schema rather than a reuse of `GetVibeAppLogsPathVarsSchema`: that one
 * belongs to a `ZVibe` descriptor and is consumed by the API codegen, and this
 * route is deliberately outside that surface. Sharing it would tie a raw
 * `@Res()` route's validation to a generated client's contract, which is the
 * coupling this whole file exists to avoid.
 */
export const GetVibeAppLogStreamPathVarsSchema = z
  .object({
    /**
     * The app whose logs to follow. Resolved ORG-SCOPED: an app in another
     * organization is a miss, indistinguishable from an id that never existed.
     */
    appId: z.string().uuid()
  })
  .strict();

export type GetVibeAppLogStreamPathVars = z.infer<typeof GetVibeAppLogStreamPathVarsSchema>;

/**
 * Why a follow stopped.
 *
 * One value, and the honest count is one. A follow does not end the way a build
 * log ends — there is no "the build finished", and an empty follow is not
 * ambiguous the way an empty build log is, so none of
 * `VIBE_BUILD_LOG_END_REASONS`' three cases translate.
 *
 * - `upstream-closed` — the tenant gateway closed its side cleanly, which it
 *   does on its own duration cap. Nothing is wrong and nothing was lost; a
 *   client that wants to keep following reconnects with a fresh `from`.
 *
 * A client that disconnects first is deliberately NOT a reason here: the socket
 * is already gone, so there is nobody to tell. An enum rather than a bare string
 * so a second reason is an additive change the console's exhaustive switch
 * catches, instead of a new sentence nobody parses.
 */
export const VIBE_APP_LOG_STREAM_END_REASONS = ["upstream-closed"] as const;
export const VibeAppLogStreamEndReasonSchema = z.enum(VIBE_APP_LOG_STREAM_END_REASONS);
export type VibeAppLogStreamEndReason = z.infer<typeof VibeAppLogStreamEndReasonSchema>;

/**
 * A frame on the runtime-log stream.
 *
 * - `lines` — append these, oldest first within the frame. The only frame that
 *             carries content. A poll that found nothing emits no frame at all;
 *             the adapter's keepalive comment covers a quiet app.
 * - `end`   — the follow stopped and the connection is closing. Terminal.
 * - `error` — the stream broke. Terminal, and `end` does NOT follow, exactly as
 *             on the build-log stream. The console keeps every line already
 *             rendered and shows the failure beneath them, because a partial log
 *             is the most valuable thing on screen when a stream dies.
 */
export const VibeAppLogStreamEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("lines"),
    lines: z.array(VibeLogLineSchema)
  }),
  z.object({
    type: z.literal("end"),
    reason: VibeAppLogStreamEndReasonSchema
  }),
  z.object({
    type: z.literal("error"),
    message: z.string()
  })
]);
export type VibeAppLogStreamEvent = z.infer<typeof VibeAppLogStreamEventSchema>;
